import Highcharts, {type Options} from 'highcharts'
import highchartsAccessibility from 'highcharts/modules/accessibility'
import highchartsExportData from 'highcharts/modules/export-data'
import highchartsExporting from 'highcharts/modules/exporting'
import highchartsOfflineExporting from 'highcharts/modules/offline-exporting'
import HighchartsReact from 'highcharts-react-official'
import {merge} from 'lodash'
import defaultOptions, {yAxisConfig} from './chart-theme'
import ChartCardContext from './context'
import {useContext} from 'react'
import type {Size} from '../shared'

// Init Highcharts modules
highchartsAccessibility(Highcharts)
highchartsExportData(Highcharts)
highchartsExporting(Highcharts)
highchartsOfflineExporting(Highcharts)

Highcharts.setOptions({
  lang: {
    decimalPoint: '.',
    thousandsSep: ',',
  },
})

export const chartHeights: {[size in Size]: string} = {
  xl: '432px',
  large: '320px',
  medium: '256px',
  small: '128px',
  sparkline: '128px',
}

export interface ChartProps {
  colors?: string[]
  overrideOptionsNotRecommended?: Highcharts.Options
  plotOptions?: Highcharts.PlotOptions
  series: Highcharts.SeriesOptionsType[]
  type: string
  xAxisTitle: string
  xAxisOptions?: Highcharts.XAxisOptions
  yAxisTitle?: string
  yAxisOptions?: Highcharts.YAxisOptions | Highcharts.YAxisOptions[]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasPropertyWithValue(obj: Record<string, any>, key: string, value: string) {
  if (typeof obj !== 'object' || obj === null) {
    return false
  }
  if (obj[key] === value) {
    return true
  } else {
    for (const prop in obj) {
      if (obj[prop] !== undefined) {
        return hasPropertyWithValue(obj[prop], key, value)
      }
    }
  }
  return false
}

export function Chart({
  colors,
  overrideOptionsNotRecommended,
  plotOptions,
  series,
  type = 'line',
  xAxisTitle,
  xAxisOptions,
  yAxisTitle,
  yAxisOptions = {},
}: ChartProps) {
  const {title, description, size, chartRef} = useContext(ChartCardContext)
  const legendVisible = series.length > 1 && size !== 'sparkline'
  const verticalLegendOrientation =
    series.length > 3
      ? {
          align: 'right',
          layout: 'vertical',
          verticalAlign: 'middle',
        }
      : {}

  const yAxisOptionsArray = Array.isArray(yAxisOptions) ? yAxisOptions : [yAxisOptions]

  const hasStacking = plotOptions && hasPropertyWithValue(plotOptions, 'stacking', 'normal')

  const options: Options = merge(
    {},
    defaultOptions,
    {
      chart: {
        type,
        height: typeof size === 'number' ? size : chartHeights[size],
        ...(legendVisible ? {marginTop: 32} : {}),
      },
      colors,
      exporting: {
        chartOptions: {
          title: {
            text: title,
          },
          caption: {
            text: description,
          },
        },
        filename: title,
      },
      lang: {
        accessibility: {
          chartContainerLabel: `${title}. Interactive chart.`,
          navigator: {
            groupLabel: `${title} Axis zoom`,
          },
        },
      },
      legend: {
        enabled: legendVisible,
        ...verticalLegendOrientation,
      },
      plotOptions: merge(
        {},
        defaultOptions.plotOptions,
        {
          series: {
            marker: {
              enabled: size !== 'sparkline',
            },
            enableMouseTracking: size !== 'sparkline',
          },
        },
        plotOptions,
      ),
      series,
      tooltip: {
        enabled: size !== 'sparkline',
        shared: hasStacking,
      },
      xAxis: merge(
        {},
        {
          visible: size !== 'sparkline',
          gridLineWidth: size !== 'sparkline' ? 1 : 0,
          title: {
            text: size !== 'sparkline' ? xAxisTitle : undefined,
          },
        },
        xAxisOptions,
      ),
      yAxis: yAxisOptionsArray.map(yAxisConsumerOption => {
        if (!yAxisConsumerOption) return
        return merge(
          {},
          yAxisConfig,
          {
            visible: size !== 'sparkline',
            gridLineWidth: size !== 'sparkline' ? 1 : 0,
            title: {
              text: size !== 'sparkline' ? yAxisTitle : undefined,
            },
          },
          yAxisConsumerOption,
        )
      }),
    },
    overrideOptionsNotRecommended,
  )

  // Apply a linear gradient fill to areaspline series
  if (options.series && options.colors) {
    let colorIndex = 0
    const colorfulSeries: typeof options.series = []
    for (const singleSeries of options.series) {
      if (singleSeries.type === 'areaspline') {
        let color
        if (singleSeries.color) {
          color = singleSeries.color
        } else {
          color = options.colors[colorIndex] as string
          colorIndex++
        }

        singleSeries.fillColor = {
          linearGradient: {x1: 0, x2: 0, y1: 0, y2: 1},
          stops: [
            [0, `color-mix(in srgb, ${color} 25%, transparent)`],
            [1, `color-mix(in srgb, ${color} 1%, transparent)`],
          ],
        }
        colorfulSeries.push(singleSeries)

        // After available colors run out, cycle through again
        if (colorIndex > options.colors.length - 1) {
          colorIndex = 0
        }
      } else {
        colorfulSeries.push(singleSeries)
      }
    }
    options.series = colorfulSeries
  }

  return <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />
}
Chart.displayName = 'ChartCard.Chart'
